import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { DATE_TIME_OPERATORS, QueryOperator } from "types";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import {
  createAndUpdateDateFilter,
  getQueryValuesForFilter,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import useVqlParams from "hooks/useVqlParams";
import { useDateTimeQueryModal } from "hooks/UseQueryModal/useDateTimeQueryModal";
import BaseQueryModal from "../CustomMetadataQueryModal/BaseQueryModal";
import { DateTimeIcon } from "assets/icons/CustomMetadataIcons";
import { Dayjs } from "dayjs";
import DateTimeSelector from "../DataTimeSelector";
import {
  formatDateForAPI,
  formatDateRangeForAPI,
  parseDateFromAPI,
  parseDateRangeFromAPI,
} from "helpers/utility/customMetadataHelpers";
import { setSelectedQueryModal } from "redux/SingleDataset/actions";

interface InsertionTimeModalProps {
  editQueryId?: string;
}
export default function InsertionTimeModal({
  editQueryId,
}: InsertionTimeModalProps) {
  const dispatch = useDispatch();

  const modalRef = useRef<HTMLDivElement>(null);

  const { findVqlQuery, addVqlQuery, updateVqlQuery, vqlQueries } =
    useVqlParams();

  // Load existing query (if editing)
  const oldQuery = editQueryId ? findVqlQuery(editQueryId, "created_at") : null;
  const oldOp = oldQuery?.created_at?.op;

  const defaultOperator = oldOp
    ? convertAPIOperator(oldOp)
    : QueryOperator.GREATER_THAN;

  // operator state
  const [operator, setOperator] = useState<QueryOperator>(defaultOperator);

  // compute mode from operator
  const getMode = (op: QueryOperator): "Single" | "Range" =>
    op === QueryOperator.BETWEEN ? "Range" : "Single";

  const [mode, setMode] = useState<"Single" | "Range">(getMode(operator));

  useEffect(() => setMode(getMode(operator)), [operator]);

  const existingQuery = useMemo(() => {
    return (
      vqlQueries.find(
        (q) =>
          isVqlQuery(q, "created_at") &&
          q.created_at &&
          convertAPIOperator(q.created_at.op) === operator,
      ) || null
    );
  }, [vqlQueries, operator]);

  const queryValues = useMemo(() => {
    return getQueryValuesForFilter(
      editQueryId,
      oldQuery,
      operator,
      defaultOperator,
      existingQuery,
      "created_at",
    );
  }, [editQueryId, oldQuery, defaultOperator, operator, existingQuery]);

  const initialValue = useCallback(() => {
    return {
      single: !Array.isArray(queryValues)
        ? queryValues
          ? parseDateFromAPI(queryValues)
          : null
        : null,
      range: Array.isArray(queryValues)
        ? parseDateRangeFromAPI(queryValues as [string, string])
        : null,
    };
  }, [queryValues]);

  const [value, setValue] = useState<{
    single: Dayjs | null;
    range: [Dayjs, Dayjs] | null;
  }>(() => initialValue());

  useEffect(() => {
    setValue(initialValue());
    // eslint-disable-next-line
  }, [operator]);

  const previousOperator =
    existingQuery && isVqlQuery(existingQuery, "created_at")
      ? convertAPIOperator(existingQuery?.created_at?.op)
      : null;

  const submitQuery = useCallback(
    (op: QueryOperator, v: typeof value) => {
      const queryId = editQueryId || existingQuery?.id;

      if (op === QueryOperator.BETWEEN) {
        // Range mode
        if (!v.range) return;
        const [start, end] = formatDateRangeForAPI(v.range)!;
        if (!start || !end) return;

        const vqlQuery = createAndUpdateDateFilter(
          [start, end],
          op as any,
          queryId,
        );
        queryId ? updateVqlQuery(vqlQuery) : addVqlQuery(vqlQuery);
      } else {
        // Single mode
        if (!v.single) return;
        const singleValue = formatDateForAPI(v.single)!;
        if (!singleValue) return;

        const vqlQuery = createAndUpdateDateFilter(
          singleValue,
          op as any,
          queryId,
        );
        queryId ? updateVqlQuery(vqlQuery) : addVqlQuery(vqlQuery);
      }
      dispatch(setSelectedQueryModal(null));
    },
    [editQueryId, existingQuery?.id, dispatch, updateVqlQuery, addVqlQuery],
  );

  const handleSubmit = () => submitQuery(operator, value);

  const isValid =
    mode === "Single"
      ? !!value.single
      : !!(
          value.range &&
          value.range.length === 2 &&
          value.range[0] &&
          value.range[1]
        );

  const { isSubmitDisabled } = useDateTimeQueryModal({
    inputValue: value,
    previousValue: queryValues,
    selectedOperator: operator,
    initialOperator: previousOperator,
    isValid,
  });

  return (
    <BaseQueryModal<QueryOperator>
      modalRef={modalRef}
      keepOpenOnLoading
      operators={{
        options: [...DATE_TIME_OPERATORS], // spreads into a new mutable array
        selected: operator,
        onSelect: setOperator,
      }}
      onSubmit={handleSubmit}
      isSubmitDisabled={isSubmitDisabled}
      title="Date Filter"
      icon={<DateTimeIcon />}
    >
      <DateTimeSelector mode={mode} value={value} onChange={setValue} />
    </BaseQueryModal>
  );
}
